/**
 * The landing page.
 *
 * One dashboard, composed from cards that each gate themselves on a permission, rather
 * than three role-specific dashboards. A team lead is an employee AND an approver AND a
 * project manager; picking one "role dashboard" for them means hiding two thirds of what
 * they need. Cards appear when the underlying permission is held, so the page composes
 * itself for whoever is looking at it.
 */
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarDays, Clock, Inbox, Receipt } from 'lucide-react';

import { useAuth } from '@frontend/auth/AuthProvider';
import { Can } from '@frontend/auth/ProtectedRoute';
import { useMyLeave } from '@frontend/api/hooks/leave';
import { useTimesheetPeriods } from '@frontend/api/hooks/timesheets';
import { usePendingApprovals } from '@frontend/api/hooks/workflows';
import { useMyOnboarding } from '@frontend/api/hooks/onboarding';
import { useMyWorkAuth } from '@frontend/api/hooks/workAuth';
import { formatDateRange, formatHours, humanise } from '@frontend/lib/format';
import { Button } from '@frontend/components/ui/Button';
import { Banner, PageHeader, StatCard } from '@frontend/components/ui/Page';
import { StatusBadge } from '@frontend/components/ui/StatusBadge';
import { Skeleton } from '@frontend/components/ui/States';
import { WorkAuthBanner } from '@frontend/pages/WorkAuth/WorkAuthBanner';

export function Dashboard(): JSX.Element {
  const { user } = useAuth();
  const periods = useTimesheetPeriods(undefined, 3);
  const leave = useMyLeave();
  const approvals = usePendingApprovals();
  const onboarding = useMyOnboarding();
  const workAuth = useMyWorkAuth();

  const currentPeriod = periods.data?.periods.find(
    (row) => row.state === 'OPEN' || row.state === 'DRAFT' || row.state === 'REJECTED',
  );
  const latePeriods = (periods.data?.periods ?? []).filter((row) => row.isLate);
  const pendingCount = approvals.data?.items.length ?? 0;
  const overdueApprovals = (approvals.data?.items ?? []).filter((item) => item.isOverdue).length;

  const firstName = user?.firstName ?? user?.displayName?.split(' ')[0] ?? 'there';

  return (
    <div>
      <PageHeader
        title={`Good to see you, ${firstName}`}
        description="Everything that needs you, in one place."
      />

      {/* Work authorisation is a legal gate — it goes above everything else. */}
      <WorkAuthBanner record={workAuth.data?.record ?? null} className="mb-4" />

      {latePeriods.length > 0 && (
        <Banner tone="warning" className="mb-4" title="You have a timesheet past its due date">
          {latePeriods.map((row) => (
            <div key={row.period.startDate}>
              {formatDateRange(row.period.startDate, row.period.endDate)} was due {row.dueDate}.{' '}
              <Link to={`/timesheets?date=${row.period.startDate}`} className="font-medium text-content-link">
                Open it
              </Link>
            </div>
          ))}
        </Banner>
      )}

      {onboarding.data?.instance != null && onboarding.data.instance.status !== 'COMPLETED' && (
        <Banner tone="info" className="mb-4" title="Your onboarding is still in progress">
          {onboarding.data.completedSteps ?? 0} of {onboarding.data.totalSteps ?? 0} steps done.{' '}
          <Link to="/onboarding" className="font-medium text-content-link">
            Continue your checklist
          </Link>
        </Banner>
      )}

      <div className="mb-4 grid grid-cols-1 gap-3 tablet:grid-cols-2 desktop:grid-cols-4">
        {periods.isLoading ? (
          <Skeleton className="h-24 rounded-lg" />
        ) : (
          <StatCard
            label="This period"
            value={currentPeriod === undefined ? '—' : formatHours(currentPeriod.totalHours)}
            hint={
              currentPeriod === undefined
                ? 'No open period'
                : `${formatDateRange(currentPeriod.period.startDate, currentPeriod.period.endDate)} · due ${currentPeriod.dueDate}`
            }
            icon={<Clock className="h-4 w-4" />}
            data-testid="hours-card"
          />
        )}

        {leave.isLoading ? (
          <Skeleton className="h-24 rounded-lg" />
        ) : (
          <StatCard
            label="Leave available"
            value={
              (leave.data?.balances.length ?? 0) === 0
                ? '—'
                : (leave.data?.balances[0]?.availableDays ?? '—')
            }
            hint={
              (leave.data?.balances.length ?? 0) === 0
                ? 'No balances yet'
                : `${humanise(leave.data?.balances[0]?.leaveType ?? '')} days remaining`
            }
            icon={<CalendarDays className="h-4 w-4" />}
            data-testid="leave-card"
          />
        )}

        <Can permission="workflows.instance.read">
          <StatCard
            label="Waiting on you"
            value={approvals.isLoading ? '—' : pendingCount}
            hint={
              overdueApprovals > 0
                ? `${overdueApprovals} past their service level`
                : pendingCount === 0
                  ? 'Nothing to decide'
                  : 'Approvals across every module'
            }
            tone={overdueApprovals > 0 ? 'warning' : 'neutral'}
            icon={<Inbox className="h-4 w-4" />}
            data-testid="approvals-card"
          />
        </Can>

        <Can permission="spending.expense.submit">
          <StatCard
            label="Expenses"
            value="—"
            hint="Submit a claim or check where an existing one is"
            icon={<Receipt className="h-4 w-4" />}
            data-testid="expenses-card"
          />
        </Can>
      </div>

      <div className="grid grid-cols-1 gap-4 desktop:grid-cols-2">
        <section className="surface-card">
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <h2 className="text-row font-semibold text-content">Recent timesheets</h2>
            <Link to="/timesheets/history" className="text-body text-content-link">
              All periods
            </Link>
          </div>
          <ul className="divide-y divide-line-subtle">
            {(periods.data?.periods ?? []).slice(-4).reverse().map((row) => (
              <li key={row.period.startDate} className="flex items-center gap-3 px-4 py-2.5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-row text-content">
                    {formatDateRange(row.period.startDate, row.period.endDate)}
                  </p>
                  <p className="text-body text-content-tertiary">{formatHours(row.totalHours)} hours</p>
                </div>
                <StatusBadge status={row.state} />
                <Link
                  to={`/timesheets?date=${row.period.startDate}`}
                  className="text-content-tertiary hover:text-content"
                  aria-label={`Open ${row.period.label}`}
                >
                  <ArrowRight aria-hidden className="h-4 w-4" />
                </Link>
              </li>
            ))}
            {(periods.data?.periods.length ?? 0) === 0 && (
              <li className="px-4 py-6 text-center text-row text-content-secondary">
                No timesheet periods yet.
              </li>
            )}
          </ul>
        </section>

        <section className="surface-card">
          <div className="flex items-center justify-between border-b border-line px-4 py-3">
            <h2 className="text-row font-semibold text-content">My leave</h2>
            <Link to="/leave" className="text-body text-content-link">
              All leave
            </Link>
          </div>
          {(leave.data?.requests.length ?? 0) === 0 ? (
            <div className="px-4 py-6 text-center">
              <p className="text-row text-content-secondary">
                No leave booked this year.
              </p>
              <Link to="/leave/request" className="mt-2 inline-block">
                <Button size="sm" variant="primary">
                  Request leave
                </Button>
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-line-subtle">
              {(leave.data?.requests ?? []).slice(0, 4).map((request) => (
                <li key={request.id} className="flex items-center gap-3 px-4 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-row text-content">{humanise(request.leaveType)}</p>
                    <p className="text-body text-content-tertiary">
                      {formatDateRange(request.fromDate, request.toDate)} · {request.days} days
                    </p>
                  </div>
                  <StatusBadge status={request.status} />
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}
